import { useState } from 'react';
import { useFormacao, type FormacaoParticipante } from '@/context/FormacaoContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';

interface Props {
  participante: FormacaoParticipante & { nomeFormacao?: string };
  onClose: () => void;
  onSuccess?: () => void;
}

const DIMENSOES = [
  { key: 'conteudo',     label: 'Conteúdo',           icon: 'ri-book-2-line' },
  { key: 'formador',     label: 'Formador',            icon: 'ri-user-star-line' },
  { key: 'organizacao',  label: 'Organização',         icon: 'ri-calendar-check-line' },
  { key: 'materiais',    label: 'Materiais',           icon: 'ri-file-text-line' },
  { key: 'aplicacao',    label: 'Aplicabilidade',      icon: 'ri-lightbulb-line' },
];

function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="whitespace-nowrap w-7 h-7 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
        >
          {(hover || value) >= star
            ? <i className="ri-star-fill text-lg text-amber-400" />
            : <i className="ri-star-line text-lg text-zinc-200" />
          }
        </button>
      ))}
      {value > 0 && (
        <span className="text-[11px] font-semibold text-zinc-500 ml-1">
          {['', 'Mau', 'Insuficiente', 'Suficiente', 'Bom', 'Excelente'][value]}
        </span>
      )}
    </div>
  );
}

export default function AvaliarParticipacaoModal({ participante, onClose, onSuccess }: Props) {
  useModalOverflow();
  const { animating, handleClose } = useModalAnimation(onClose);
  const { atualizarParticipante, saving } = useFormacao();

  const jaAvaliado = participante.avaliacao_nota !== null;

  // Nota global (1-5) — média das dimensões ou nota direta
  const [notas, setNotas] = useState<Record<string, number>>({
    conteudo: 0,
    formador: 0,
    organizacao: 0,
    materiais: 0,
    aplicacao: 0,
  });
  const [comentario, setComentario] = useState(participante.avaliacao_comentario ?? '');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Se já tem avaliação, pré-preencher nota global em todas as dimensões
  useState(() => {
    if (jaAvaliado && participante.avaliacao_nota) {
      setNotas({
        conteudo: participante.avaliacao_nota,
        formador: participante.avaliacao_nota,
        organizacao: participante.avaliacao_nota,
        materiais: participante.avaliacao_nota,
        aplicacao: participante.avaliacao_nota,
      });
    }
  });

  const notasPreenchidas = Object.values(notas).filter((v) => v > 0);
  const mediaNotas = notasPreenchidas.length > 0
    ? Math.round(notasPreenchidas.reduce((a, b) => a + b, 0) / notasPreenchidas.length)
    : 0;

  const todasPreenchidas = notasPreenchidas.length === DIMENSOES.length;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!todasPreenchidas) {
      setSubmitError('Por favor avalie todas as dimensões antes de submeter.');
      return;
    }
    const ok = await atualizarParticipante(participante.id, {
      avaliacao_nota: mediaNotas,
      avaliacao_comentario: comentario.trim() || null,
      avaliacao_data: new Date().toISOString(),
    });
    if (ok) {
      onSuccess?.();
      onClose();
    } else {
      setSubmitError('Não foi possível guardar a avaliação. Tente novamente.');
    }
  }

  const nome = participante.funcionario_nome ?? participante.funcionario_id;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto mx-4 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}>
        {/* Header */}
        <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-start justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-500 flex-shrink-0 mt-0.5">
              <i className="ri-star-line text-white text-base" />
            </div>
            <div className="min-w-0">
              <h2 className="text-[16px] font-bold text-zinc-900">Avaliar Participação</h2>
              <p className="text-[11px] text-zinc-400 truncate max-w-[280px]">
                {nome}
                {participante.nomeFormacao && (
                  <span className="text-zinc-300"> · {participante.nomeFormacao}</span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-5 space-y-5">
          {/* Aviso se já avaliado */}
          {jaAvaliado && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 text-amber-700 text-[12px] px-3 py-2.5 rounded-lg">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-sm" />
              </div>
              Esta participação já foi avaliada com <strong className="ml-1">{participante.avaliacao_nota}/5</strong>. Pode substituir a avaliação.
            </div>
          )}

          {/* Dimensões */}
          <div className="space-y-3">
            <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-wide">Avaliação por Dimensão</p>
            {DIMENSOES.map((d) => (
              <div key={d.key} className="flex items-center justify-between gap-3 py-2 border-b border-zinc-50 last:border-b-0">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-zinc-100 flex-shrink-0">
                    <i className={`${d.icon} text-sm text-zinc-500`} />
                  </div>
                  <span className="text-[13px] font-medium text-zinc-700">{d.label}</span>
                </div>
                <StarRating
                  value={notas[d.key]}
                  onChange={(v) => setNotas((prev) => ({ ...prev, [d.key]: v }))}
                />
              </div>
            ))}
          </div>

          {/* Média calculada */}
          {todasPreenchidas && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold text-amber-600 uppercase tracking-wide">Nota Global (média)</p>
                <div className="flex items-center gap-1.5 mt-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <div key={s} className="w-5 h-5 flex items-center justify-center">
                      {mediaNotas >= s
                        ? <i className="ri-star-fill text-base text-amber-400" />
                        : <i className="ri-star-line text-base text-amber-400" />
                      }
                    </div>
                  ))}
                  <span className="text-[14px] font-bold text-amber-700 ml-1">{mediaNotas}/5</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[11px] text-amber-600">
                  {['', 'Mau', 'Insuficiente', 'Suficiente', 'Bom', 'Excelente'][mediaNotas]}
                </p>
              </div>
            </div>
          )}

          {/* Comentário */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
              Comentário / Observações
              <span className="text-[10px] font-normal text-zinc-400 ml-1">opcional</span>
            </label>
            <textarea
              rows={3}
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              maxLength={500}
              placeholder="Pontos fortes, sugestões de melhoria, observações gerais..."
              className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 resize-none"
            />
            <p className="text-[10px] text-zinc-400 text-right mt-0.5">{comentario.length}/500</p>
          </div>

          {submitError && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-600 text-[12px] px-3 py-2 rounded-lg">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-sm" />
              </div>
              {submitError}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
            <button
              type="button"
              onClick={handleClose}
              className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !todasPreenchidas}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-amber-500 rounded-lg hover:bg-amber-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>
                  A guardar...
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-line text-sm" /></div>
                  {jaAvaliado ? 'Atualizar Avaliação' : 'Submeter Avaliação'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
